import CellAnnotationValueViewer from 'components/CellAnnotationValueViewer';
import type { CellAnnotationCapabilities } from 'components/CellAnnotationValueViewer';
import { withMoneyChip } from 'components/CellAnnotationValueViewer/components/MoneyChipValueViewer';
import { buildCellKey } from 'components/CellAnnotationValueViewer/types';
import type {
  CellAnnotationCellKey,
  CellAnnotationConfig,
} from 'components/CellAnnotationValueViewer/types';
import FastValueViewer from 'components/ScalarSpreadsheet/utilities/FastValueViewer';
import type { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { useCellAnnotationStore } from 'store/useCellAnnotationStore';

// Single source of truth for how a money-amount cell on an acquisition / sale
// / distribution row gets the cell-annotation viewer attached. Each call
// returns a new cell - the original is left untouched if the row hasn't been
// persisted yet (no id), since annotations require a server-side parent.
//
// `parentType` must match the Django ContentType label (lowercase model name
// without underscores): `sharesacquisition`, `sharessale`, or
// `cashdistribution`. See backend ContentType.objects.get_for_model on each
// child model in apps/funds/models.

// Pre-FX is single_cell with no metadata columns - the popout owns its own
// fields (amount + source currency). Keeping the config local avoids a
// per-host registry of identical configs.
const PRE_FX_CONFIG: CellAnnotationConfig = {
  annotationType: 'pre_fx_currency',
  rowMode: 'single_cell',
  columns: [],
};

// Hover-bar opt-ins for shares-ledger child money cells. Only Pre-FX is
// enabled today: the cross-reference write path through the child writables
// is wired but the read-side hydration / display surface for cross-ref on
// these rows isn't built yet. Add `'cross_reference'` to `enabled` once that
// surface lands so the bar surfaces both icons without any other changes.
const MONEY_BAR_CAPABILITIES: CellAnnotationCapabilities = {
  enabled: ['pre_fx_currency'],
};

// Synthetic-id prefix for unsaved rows. The save-time wire splitter
// (`splitForFodSave`) treats any parentId starting with this prefix as a
// reference to a not-yet-persisted child row; the cap-table save hook then
// looks the entry up by `acquisition_ref` / `sale_ref` / row UUID and folds
// it onto the new row before the parent save creates it.
//
// The prefix uses an underscore (not a colon) because the cellKey format is
// colon-delimited (`<parentType>:<parentId>:<fieldAlias>:<annotationType>`)
// and `parseCellKey` strict-splits on `:` - embedding a colon in the
// parentId would break parsing.
const SYNTHETIC_PARENT_PREFIX = 'ref_';

// Memoize the chip-wrapping HOC per inner viewer so re-parsing the table
// doesn't churn component identity (which would break React.memo on the
// rendered cell and cause flicker on busy ledgers).
type AnyViewer = React.ComponentType<any>;
const chipWrapCache: WeakMap<AnyViewer, AnyViewer> = new WeakMap();
const getChipWrapped = (Inner: AnyViewer): AnyViewer => {
  let wrapped = chipWrapCache.get(Inner);
  if (!wrapped) {
    wrapped = withMoneyChip(Inner as React.ComponentType<import('common/types/scalarSpreadsheet').ValueViewerProps>);
    chipWrapCache.set(Inner, wrapped);
  }
  return wrapped;
};

export const buildSyntheticParentId = (parentRef: string): string => `${SYNTHETIC_PARENT_PREFIX}${parentRef}`;
export const isSyntheticParentId = (parentId: unknown): parentId is string =>
  typeof parentId === 'string' && parentId.startsWith(SYNTHETIC_PARENT_PREFIX);
export const extractRefFromSyntheticParentId = (parentId: string): string | null => {
  if (!parentId.startsWith(SYNTHETIC_PARENT_PREFIX)) {
    return null;
  }
  return parentId.slice(SYNTHETIC_PARENT_PREFIX.length);
};

export interface ApplyPreFxAnnotationParams {
  cell: ICell;
  // Django ContentType label (lowercase model name). `security` is the OIP
  // host on the cap table; the others are fund-ownership ledger money cells.
  parentType: 'sharesacquisition' | 'sharessale' | 'cashdistribution' | 'security';
  // Persisted parent id. When null/0/missing the row is fresh and
  // `parentRef` (e.g. `acquisition_ref`/`sale_ref`) is used to build a
  // synthetic cellKey instead - wires the bar + popout on unsaved rows so
  // the user can enter Pre-FX before the row exists server-side.
  parentId: number | string | null | undefined;
  // Stable client-side identifier for the row (UUID). Required when
  // `parentId` is missing; the synthetic cellKey lets the user enter Pre-FX
  // pre-save, and the save flow resolves the synthetic id to the new
  // server-side row by ref.
  parentRef?: string | null;
  fieldAlias: string;
  defaultCurrency?: string;
  // Host currency for the live FX preview (cap-table currency for FOD,
  // company IRR currency for CFL when this util is reused). When omitted,
  // the preview is skipped and the host cell stays whatever the user typed.
  hostCurrency?: string;
  // Date used to fetch the FX rate for the preview. ISO YYYY-MM-DD or a
  // getter that returns the latest date from the live cell map (FOD
  // freshly-added rows: the spreadsheet's internal date cell updates
  // before `acquisitionsData.purchase_date` does, so the getter form
  // avoids capturing a stale snapshot at parse-cells time).
  transactionDate?: string | (() => string | undefined | null);
  // Parent supplies a callback that patches its rows state with the
  // converted host amount. Without this the popout can still edit the
  // annotation but the host cell won't update until save.
  onHostPreview?: (value: number | null) => void;
}

// Returns the cell with annotation wiring attached. When `parentId` is
// missing the cell is wired with a synthetic cellKey derived from
// `parentRef` so unsaved rows still get the hover bar + popout - required
// for the "enter Pre-FX before Cost Basis" workflow on fresh acquisitions.
// Falls through unchanged only when neither id nor ref is available (no
// stable identifier means we can't address the store).
export const applyPreFxAnnotation = ({
  cell,
  parentType,
  parentId,
  parentRef,
  fieldAlias,
  defaultCurrency,
  hostCurrency,
  transactionDate,
  onHostPreview,
}: ApplyPreFxAnnotationParams): ICell => {
  const hasPersistedId =
    parentId !== null && parentId !== undefined && parentId !== 0 && parentId !== '';

  let resolvedParentId: number | string;
  if (hasPersistedId) {
    resolvedParentId = parentId as number | string;
  } else if (parentRef) {
    resolvedParentId = buildSyntheticParentId(parentRef);
  } else {
    return cell;
  }

  const cellKey: CellAnnotationCellKey = buildCellKey(
    parentType,
    resolvedParentId,
    fieldAlias,
    'pre_fx_currency'
  );

  // Wrap the host's amount viewer with the chip layout so the currency chip
  // renders inside the cell on the left - that chip is the in-cell trigger
  // that opens the Pre-FX popout via `onChipClick` below. Falls back to
  // FastValueViewer when the host didn't supply a viewer (default cell
  // rendering); the chip still attaches and stays clickable.
  const innerViewer = (cell.valueViewer ?? cell.originalValueViewer ?? FastValueViewer) as AnyViewer;
  const chipWrappedInner = getChipWrapped(innerViewer);

  return {
    ...cell,
    originalValueViewer: chipWrappedInner as ICell['originalValueViewer'],
    valueViewer: CellAnnotationValueViewer,
    cellAnnotation: PRE_FX_CONFIG,
    cellAnnotationCellKey: cellKey,
    cellAnnotationBarCapabilities: MONEY_BAR_CAPABILITIES,
    defaultPreFxCurrency: defaultCurrency,
    preFxHostCurrency: hostCurrency,
    preFxTransactionDate: transactionDate,
    onPreFxHostPreview: onHostPreview,
    onChipClick: () => useCellAnnotationStore.getState().openPopout(cellKey),
  };
};

export default applyPreFxAnnotation;
